import { ReactNode } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { ActivityOfType } from '@/components/activities/Activity';
import { BaseActivity, BaseActivityText } from '@/components/activities/BaseActivity/BaseActivity';
import InlineText from '@/design_system/Typography/InlineText';
import IconValidation from '@/icons/Validation.svg';
import { EXCEPTIONAL_BRAND_COVERAGE_REASONS } from '@/models/article';
import { useGetDefectName } from '@/models/defectType';

export const ArticleExceptionalBrandCoverageUpdatedActivity = ({
  activity,
}: {
  activity: ActivityOfType<'article_exceptional_brand_coverage_updated'>;
}) => {
  const { i18n } = useLingui();

  const getDefectName = useGetDefectName();
  const creatorName = activity.creator.name;
  const defectName = getDefectName(activity.articleDefect);
  const reason = EXCEPTIONAL_BRAND_COVERAGE_REASONS.find(({ id }) => id === activity.data.reason);
  const reasonText = reason ? i18n._(reason.text) : null;
  const detailsText = [reasonText, activity.data.originalRequestReference]
    .filter(Boolean)
    .join(' • ');

  let message: ReactNode;

  if (!activity.data.after) {
    message = (
      <Trans id="activities.article-exceptional-brand-coverage-updated.removed">
        <InlineText weight="medium">{creatorName}</InlineText> removed exceptional brand coverage on
        defect {defectName}
      </Trans>
    );
  } else if (
    activity.data.step === 'creation' ||
    activity.data.taskType === 'choose_article_service'
  ) {
    message = (
      <Trans id="activities.article-exceptional-brand-coverage-updated.requested">
        <InlineText weight="medium">{creatorName}</InlineText> requested exceptional brand coverage
        for defect {defectName} ({detailsText})
      </Trans>
    );
  } else {
    message = (
      <Trans id="activities.article-exceptional-brand-coverage-updated.confirmed">
        <InlineText weight="medium">{creatorName}</InlineText> confirmed exceptional brand coverage
        for defect {defectName} ({detailsText}) — covered by brand decision
      </Trans>
    );
  }

  return (
    <BaseActivity
      createdAt={activity.createdAt}
      type="neutral"
      icon={<IconValidation />}
      message={<BaseActivityText>{message}</BaseActivityText>}
    />
  );
};
